// src/input/keyboard.ts — KeyboardEvent + mouse → ham durum (WP6; DESIGN §2 klavye/mouse eşlemesi, controls.ts tabloları).
// Kurallar: Ctrl/Alt/Cmd basılıyken HİÇBİR oyun tuşu üretilmez (tarayıcı kısayolu çakışması); `contextmenu` preventDefault
// zorunlu (sağ tık = Y) ve PENCERE genelinde: duraklatma kaplaması (#pause, tam ekran) kanvasın üstündeyken sağ tık (Y)
// alışkanlığıyla tıklayan oyuncuya tarayıcı menüsü açılmasın (tur-2 bulgusu; menü ekranında kaynak zaten detach → menü DOM'u
// etkilenmez); e.repeat kenar üretmez; odak kaybında tüm tuşlar bırakılır. Mantık DOM'suz metotlarda (keyDown/keyUp/
// mouseDown/mouseUp/mouseMove/blur) — testler bunları çağırır; `attach()` yalnız DOM olaylarını bunlara bağlar.
import { Btn } from '../core/types.ts';
import { FORBIDDEN_MODIFIERS, KEY_BUTTONS, KEY_MOVE, MOUSE_BUTTONS } from '../config/controls.ts';
import type { InputSource, RawState } from './inputManager.ts';

/** Hareket tuşu kodları (recomputeMove döngüsü için bir kez çıkarılır; sıcak yolda Object.keys yok). */
const MOVE_KEYS: readonly string[] = Object.keys(KEY_MOVE);

/** WP9 ek: `KeyboardEvent.code` boş gelirse (bazı sanal klavyeler / IME / tarayıcı otomasyonu yalnız `key` verir) `key`ten türet. */
const KEY_TO_CODE: Readonly<Record<string, string>> = {
  ' ': 'Space', Enter: 'Enter', Escape: 'Escape', Shift: 'ShiftLeft', ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown', ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
};
export function codeFromKey(key: string): string {
  if (key.length === 1) {
    const c = key.toUpperCase();
    return c >= 'A' && c <= 'Z' ? 'Key' + c : KEY_TO_CODE[key] ?? '';
  }
  return KEY_TO_CODE[key] ?? key;
}
const codeOf = (e: KeyboardEvent): string => e.code || codeFromKey(e.key);

export class KeyboardMouseSource implements InputSource {
  /** Basılı tuş kodları (küçük küme; Set tahsisi yalnız tuş olayında, poll'da yok). */
  private readonly downKeys = new Set<string>();
  private readonly downMouse = new Set<number>();
  private keyButtons = 0;      // klavye basılı Btn maskesi (tuş kümesinden türetilir)
  private mouseButtons = 0;    // mouse basılı Btn maskesi
  private moveX = 0;
  private moveZ = 0;
  private pressedSince = 0;
  private releasedSince = 0;
  private mousePressedSince = 0;
  private ndcX = 0;
  private ndcY = 0;
  private moveSeq = 0;

  private target: EventTarget | null = null;
  private elem: HTMLElement | null = null;
  // Dinleyiciler bir kez bağlanır (detach için saklanır).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const blocked = FORBIDDEN_MODIFIERS.some((m) => e[m]);
    if (this.keyDown(codeOf(e), blocked, e.repeat)) e.preventDefault();   // Space kaydırma / ok tuşları / Tab vb. engellenir
  };
  private readonly onKeyUp = (e: KeyboardEvent): void => { this.keyUp(codeOf(e)); };
  private readonly onMouseDown = (e: MouseEvent): void => {
    // macOS Ctrl+tık = sağ tık: yasak değiştiriciyle mouse da oyun tuşu üretmez.
    const blocked = FORBIDDEN_MODIFIERS.some((m) => e[m]);
    if (this.mouseDown(e.button, blocked)) e.preventDefault();
  };
  private readonly onMouseUp = (e: MouseEvent): void => { this.mouseUp(e.button); };
  private readonly onMouseMove = (e: MouseEvent): void => {
    // Hedef eleman varsa offsetX/Y (kanvasa göre, tahsissiz); yoksa pencere.
    const el = this.elem;
    const w = el ? el.clientWidth : window.innerWidth;
    const h = el ? el.clientHeight : window.innerHeight;
    const x = el ? e.offsetX : e.clientX;
    const y = el ? e.offsetY : e.clientY;
    if (w > 0 && h > 0) this.mouseMove((x / w) * 2 - 1, 1 - (y / h) * 2);
  };
  private readonly onContextMenu = (e: Event): void => { e.preventDefault(); };
  private readonly onBlur = (): void => { this.blur(); };

  /** DOM'a bağla: klavye pencereden (odak sorunu yok), mouse hedeften (kanvas). contextmenu PENCEREDE engellenir (kaplamalar dâhil). */
  attach(target: HTMLElement | Window = window): void {
    this.detach();
    this.target = target;
    this.elem = target instanceof HTMLElement ? target : null;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    target.addEventListener('mousedown', this.onMouseDown as EventListener);
    window.addEventListener('mouseup', this.onMouseUp);        // kanvas dışında bırakılsa da yakala
    target.addEventListener('mousemove', this.onMouseMove as EventListener);
    window.addEventListener('contextmenu', this.onContextMenu);   // kanvas + #pause kaplaması + butonları: bağlıyken sağ tık = Y
  }
  detach(): void {
    const t = this.target;
    if (!t) return;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    t.removeEventListener('mousedown', this.onMouseDown as EventListener);
    window.removeEventListener('mouseup', this.onMouseUp);
    t.removeEventListener('mousemove', this.onMouseMove as EventListener);
    window.removeEventListener('contextmenu', this.onContextMenu);
    this.target = null; this.elem = null;
    this.blur();
  }

  // ───────── DOM'suz mantık (testler doğrudan çağırır) ─────────

  /** Tuş basıldı. `blocked` = Ctrl/Alt/Cmd basılı → hiçbir eylem. Dönüş: olay oyuna aitti (preventDefault). */
  keyDown(code: string, blocked = false, repeat = false): boolean {
    const isGame = code in KEY_BUTTONS || code in KEY_MOVE;
    if (!isGame) return false;
    if (blocked) return false;            // Ctrl+W vb. tarayıcıya kalsın; kenar yok
    if (repeat || this.downKeys.has(code)) return true;
    this.downKeys.add(code);
    const btn = KEY_BUTTONS[code];
    if (btn !== undefined) {
      if ((this.keyButtons & btn) === 0) this.pressedSince |= btn;   // aynı Btn'in ikinci tuşu kenar üretmez
      this.keyButtons |= btn;
    }
    this.recomputeMove();
    return true;
  }
  /** Tuş bırakıldı (değiştirici olsa da her zaman bırak — aksi hâlde tuş takılı kalır). */
  keyUp(code: string): void {
    if (!this.downKeys.delete(code)) return;
    const btn = KEY_BUTTONS[code];
    if (btn !== undefined && !this.anyKeyFor(btn)) {
      this.keyButtons &= ~btn;
      this.releasedSince |= btn;
    }
    this.recomputeMove();
  }
  mouseDown(button: number, blocked = false): boolean {
    const btn = MOUSE_BUTTONS[button];
    if (btn === undefined) return false;
    if (blocked) return false;
    if (this.downMouse.has(button)) return true;
    this.downMouse.add(button);
    if ((this.mouseButtons & btn) === 0) { this.pressedSince |= btn; this.mousePressedSince |= btn; }
    this.mouseButtons |= btn;
    return true;
  }
  mouseUp(button: number): void {
    if (!this.downMouse.delete(button)) return;
    const btn = MOUSE_BUTTONS[button];
    if (btn === undefined) return;
    for (const b of this.downMouse) if (MOUSE_BUTTONS[b] === btn) return;   // orta + yan tuş aynı Btn
    this.mouseButtons &= ~btn;
    this.releasedSince |= btn;
  }
  /** İmleç NDC (−1..1, y yukarı +). Her çağrı "hareket" sayılır (aimYaw tazeliği). */
  mouseMove(ndcX: number, ndcY: number): void {
    this.ndcX = ndcX; this.ndcY = ndcY;
    this.moveSeq++;
  }
  /** Odak kaybı: her şey bırakıldı (bırakma kenarları üretilir; sim parry'yi yanlış tetiklemesin diye yalnız basılı olanlar). */
  blur(): void {
    this.releasedSince |= this.keyButtons | this.mouseButtons;
    this.downKeys.clear(); this.downMouse.clear();
    this.keyButtons = 0; this.mouseButtons = 0; this.moveX = 0; this.moveZ = 0;
  }

  /** InputSource: ham duruma katkı (OR/topla); poll arası kenar birikimleri okunduktan sonra temizlenir. */
  read(raw: RawState): void {
    raw.buttons |= this.keyButtons | this.mouseButtons;
    raw.pressedSince |= this.pressedSince;
    raw.releasedSince |= this.releasedSince;
    raw.mousePressedSince |= this.mousePressedSince;
    raw.keyMoveX += this.moveX;
    raw.keyMoveZ += this.moveZ;
    raw.mouseNdcX = this.ndcX;
    raw.mouseNdcY = this.ndcY;
    raw.mouseMoveSeq = this.moveSeq;
    this.pressedSince = 0; this.releasedSince = 0; this.mousePressedSince = 0;
  }

  private anyKeyFor(btn: Btn): boolean {
    for (const k of this.downKeys) if (KEY_BUTTONS[k] === btn) return true;
    return false;
  }
  /** WASD toplamı; karşıt tuşlar birbirini götürür; bileşenler −1..1'e kırpılır (çapraz normalizasyonu inputManager ölü bölgesinde). */
  private recomputeMove(): void {
    let x = 0, z = 0;
    for (let i = 0; i < MOVE_KEYS.length; i++) {
      const k = MOVE_KEYS[i]!;
      if (!this.downKeys.has(k)) continue;
      const v = KEY_MOVE[k]!;
      x += v[0]; z += v[1];
    }
    this.moveX = x < -1 ? -1 : x > 1 ? 1 : x;
    this.moveZ = z < -1 ? -1 : z > 1 ? 1 : z;
  }
}
